import {
  CalendarCheckIcon,
  CalendarIcon,
  MailboxIcon,
  UsersIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";
import type {
  ExternalAccountCapability,
  ExternalAccountProvider,
} from "./types";

/**
 * The accounts a tenant can connect, in the order the dialog lists them. Their logo
 * and name come from the integration catalogue, their wording from
 * `externalAccounts.providers.<id>`.
 */
export const PROVIDERS: ExternalAccountProvider[] = (
  ["google", "microsoft", "caldav", "ics", "carddav"] as const
).filter((id) => id !== "microsoft"); // Not available yet

export const ALL_CAPABILITIES = [
  {
    id: "calendar" as ExternalAccountCapability,
    name: "Calendar",
    description: "Manage your calendars and events",
    icon: CalendarIcon,
  },
  {
    id: "email" as ExternalAccountCapability,
    name: "Email",
    description: "Send and receive emails on your behalf",
    icon: MailboxIcon,
  },
  {
    id: "contacts" as ExternalAccountCapability,
    name: "Contacts",
    description: "Synchronize your contacts to your customers",
    icon: UsersIcon,
  },
  {
    id: "video" as ExternalAccountCapability,
    name: "Video meetings",
    description: "Create a Google Meet link on appointments",
    icon: VideoCameraIcon,
  },
  {
    id: "booking" as ExternalAccountCapability,
    name: "Booking",
    description: "Book appointments in this software",
    icon: CalendarCheckIcon,
  },
];

export const getAvailableCapabilities = (
  provider: ExternalAccountProvider | null,
) => {
  return ALL_CAPABILITIES.filter((cap) =>
    getAvailableCapabilityIds(provider ?? "google").includes(cap.id),
  );
};

/**
 * What each provider's accounts can be used for — the one table every picker
 * reads: a module only ever offers the accounts carrying the capability it needs
 * (`calendar`/`contacts` are synced, `video` hosts Google Meet, `booking` is a
 * booking software). The backend enforces the same table on every write
 * (`accounts/services/capabilities.ts`): keep the two in step.
 */
export const PROVIDER_CAPABILITIES: Record<
  ExternalAccountProvider,
  ExternalAccountCapability[]
> = {
  google: ["calendar", "contacts", "video"],
  microsoft: ["calendar", "contacts"],
  dav: ["calendar", "contacts"],
  caldav: ["calendar"],
  carddav: ["contacts"],
  ics: ["calendar"], // read-only subscription
  provalliance: ["booking"],
  calendly: ["booking"],
  website: ["booking"],
};

export const getAvailableCapabilityIds = (
  provider: ExternalAccountProvider,
): ExternalAccountCapability[] => PROVIDER_CAPABILITIES[provider] ?? [];

/**
 * Can this account host a Google Meet? Connected for it (`video`), or for its
 * calendar, which grants the same — backend `canHostMeeting`.
 */
export const canHostMeeting = (account: {
  provider: ExternalAccountProvider;
  status: string;
  capabilities?: { capabilities: ExternalAccountCapability[] };
}) =>
  account.provider === "google" &&
  account.status === "active" &&
  (account.capabilities?.capabilities ?? []).some(
    (capability) => capability === "video" || capability === "calendar",
  );
